<template>
  <section>
    <header>Коммерческое предложение</header>

    <VSelectV2
      v-model="bid.sellerCompany"
      label="Юр. лицо"
      :data="sellerCompanySource"

      required
      :validate-method="validateSellerCompany"
      :disabled="bid.canChangeRequisites === false"

      :class="{ attention: bid.getBundleMismatch(bid.tender.product) }"
    />

    <VFormItem
      class="VAT-requisites"
      label="НДС"
      layout="vertical"
    >
      <VSelect
        v-model="bid.vatRateSeller"
        class="VAT-size-control"
        :options="$core.dictionaries.vatRates.filter(vatRate => vatRate.lessThanOrEqualTo(bid.vatRateBuyer))"
        :clearable="false"
        :disabled="bid.sellerCompany?.isVatPayer !== true || bid.canChangeRequisites === false"
      />

      <VSelect
        v-model="localBidIsVatIncludedSeller"
        class="include-VAT-control"
        :options="$core.dictionaries.vatInclusionOptions"
        :clearable="false"
        :disabled="bid.sellerCompany?.isVatPayer !== true || bid.canChangeRequisites === false"
      />
    </VFormItem>
  </section>

  <hr>

  <section>
    <VSelect
      ref="unitInput"
      v-model="bid.unit"
      required
      data-url="units.getList"
      :clearable="false"
      layout="vertical"
      label="Единица измерения"
      :validate-method="validateUnit"
      :disabled="bid.canChangeRequisites === false"
      :class="{ attention: bid.getUnitMismatch(bid.tender.unit) }"
    />

    <VSelect
      ref="packageInput"
      v-model="bid.package"
      required
      searchable
      layout="vertical"
      :minimum-input-length="1"
      data-url="packages.getList"
      :clearable="false"
      label-width="220px"
      label="Упаковка"
      :disabled="bid.canChangeRequisites === false"
    >
      <template #option="{ option }">
        <div class="package-list-option">
          <b>{{ option.code }}</b>
          <span>{{ option.name }}</span>
        </div>
      </template>
    </VSelect>

    <VInputNumberRusV2
      ref="packageVolumeInput"
      v-model="bid.packageVolume"
      :type="Qty"
      label-width="220px"
      layout="vertical"
      label="Фасовка"
      :max="1000"
      required
      :disabled="bid.canChangeRequisites === false"
    />
  </section>

  <hr>

  <PackageCharacteristics
    :order-option="bid"
    required
    :disabled="bid.canChangeRequisites === false"
  />

  <hr>

  <section>
    <header>Параметры поставщика</header>

    <VInput
      ref="formalNameInput"
      v-model="bid.name"
      label="Наименование товара в ИС поставщика"
      layout="vertical"
      required
      :max-length="175"
      :disabled="bid.canChangeRequisites === false"
    >
      <template #helper-text>
        Наименование будет отображаться в отгрузочных документах, на транспортном ярлыке, в
        Декларации соответствия и сертификатах анализа
      </template>
    </VInput>

    <VInput
      ref="codeInput"
      v-model="bid.sellersCode"
      layout="vertical"
      label="Артикул"
      :max-length="60"
      :required="bid.sellerCompany?.isUsingEdi === true"
      :disabled="bid.canChangeRequisites === false"
    >
      <template #helper-text>
        Артикул в вашей системе учета. Будет использоваться для интеграции (обновление
        остатков, цены)
      </template>
    </VInput>

    <VSelect
      v-model="bid.manufacturer"
      required
      layout="vertical"
      label="Производитель"
      searchable
      :clearable="false"
      data-url="manufacturers.getList"
      :minimum-input-length="1"
      option-identity="id"
      :validate-method="validateManufacturer"
      option-label="name"
      :disabled="bid.canChangeRequisites === false"
      :class="{ attention: bid.getManufacturerMismatch(bid.tender.product.getDesiredManufacturersByPartner(bid.seller)) }"
    >
      <template #helper-text>
        <VHelperText
          theme="blue-flat"
        >
          Желаемые производители:
          <ul
            v-if="bid.tender.product.getDesiredManufacturersByPartner(bid.seller).length"
            class="manufacturer-list"
          >
            <li
              v-for="(item, index) in bid.tender.product.getDesiredManufacturersByPartner(bid.seller)"
              :key="index"
            >
              {{ item.name }}
            </li>
          </ul>
          <span v-else>--</span>
        </VHelperText>
      </template>
    </VSelect>

    <VTextarea
      class="bid-description"
      v-model="bid.description"
      layout="vertical"
      label="Описание"
      :disabled="bid.canChangeRequisites === false"
    >
      <template #helper-text>
        Внесите дополнительные данные на ваше усмотрение - недостающую характеристику товара
        и
        её параметр или важную особенность товара, которая выделяет ваш товар из всех
        предложений
      </template>
    </VTextarea>
  </section>

  <hr>

  <section>
    <header>Условия перевозки</header>

    <VTemperatureRangeInput
      v-model="bid.storeTemperatureRange"
      label="Температурный режим перевозки, °C"
      :max="85"
      :min="-85"
      required
      with-asterisk
      :disabled="bid.canChangeRequisites === false"
    />

    <VTextarea
      ref="storeRulesTextInput"
      v-model="bid.storeRulesText"
      label="Особые условия перевозки"
      layout="vertical"
      :max-length="300"
      :disabled="bid.canChangeRequisites === false"
    />
  </section>

  <hr>

  <section>
    <VSelect
      label="Покупатель"
      layout="vertical"
      disabled
      v-model="bid.buyer"
    />

    <VSelect
      label="Склад покупателя"
      layout="vertical"
      disabled
      v-model="bid.buyerStore"
    />
  </section>
</template>

<style lang="scss" src="./CreateTenderBidSecondary.scss"/>

<script lang="ts">
import type { UnwrapNestedRefs } from 'vue';
import type TenderBid         from '@/domains/TenderBids/TenderBid';
import type Manufacturer      from '@/domains/Manufacturers/Manufacturer';
import type Company           from '@/domains/Companies/Company';
import type Unit              from '@/domains/Units/Unit';
import Qty                    from '@/core/types/Qty';

import VHelperText            from '@/lib/components/VHelperText/VHelperText.vue';
import PackageCharacteristics from '@/domains/Offers/components/PackageCharacteristics/PackageCharacteristics.vue';
import CompaniesService from "@/domains/Companies/CompaniesService";

export default defineComponent({
  name: 'CreateTenderBidSecondary',

  components: {
    PackageCharacteristics,
    VHelperText,
  },

  props: {
    /** Развёрнутый тип: ставка приходит из reactive-состояния диалога,
     *  номинальный TenderBid туда уже не подходит. */
    bid: {
      type:     Object as PropType<UnwrapNestedRefs<TenderBid>>,
      required: true,
    },
  },

  setup() {
    return {
      /** Класс нужен шаблону для :type у VInputNumberRusV2 */
      Qty,
    };
  },

  data() {
    const sellerCompanySource = this.$core.loader.select<TenderBid>(({seller}) =>
      CompaniesService.getList({ partnerId: seller.id })
    )
      .dependsOn({seller: () => this.bid.seller })
      .preloadOnFocus();

    return {
      sellerCompanySource,
    }
  },

  computed: {
    /**
     * VSelect отдаёт элемент словаря, а в модели лежит VatRate — прокси
     * разбирает и собирает. Поиск по equals: VatRate это объект, сравнение
     * по ссылке нашло бы только тот же самый экземпляр.
     */
    localBidVatRateSeller: {
      set(value: any): void {
        this.bid.vatRateSeller = value.value;
      },
      get(): any {
        if (this.bid.sellerCompany?.isVatPayer === true) {
          return this.$core.dictionaries.vatRates.find(
            (vatRate: any) => vatRate.value.equals(this.bid.vatRateSeller),
          );
        }
        else {
          return {
            id:   0,
            name: '--',
          };
        }
      },
    },

    localBidIsVatIncludedSeller: {
      set(value: any): void {
        this.bid.isVatIncludedSeller = value.value;
      },
      get(): any {
        if (this.bid.sellerCompany?.isVatPayer === true) {
          return this.$core.dictionaries.vatInclusionOptions.find(
            (type: any) => type.value === this.bid.isVatIncludedSeller,
          );
        }
        else {
          return {
            id:   0,
            name: '--',
          };
        }
      },
    },
  },

  methods: {
    validateManufacturer(manufacturer: Manufacturer): string | null {
      const desiredManufacturers = this.bid.tender.product.getDesiredManufacturersByPartner(this.bid.seller);

      if (
        this.$core.utils.isNotEmptyArray(desiredManufacturers) === true
        && desiredManufacturers.find(item => item.id === manufacturer.id) === undefined
      ) {
        return 'Вы не можете принять участие с этим производителем';
      }
      else {
        return null;
      }
    },

    validateSellerCompany(sellerCompany: Company | null): string | null {
      // пользователь сбросил значение
      if (sellerCompany === null) return null;

      const desiredSellerCompanies = this.bid.tender.product.getDesiredSellerCompaniesByPartner(this.bid.seller);

      if (
        this.$core.utils.isNotEmptyArray(desiredSellerCompanies) === true
        && desiredSellerCompanies.find(item => item.id === sellerCompany.id) === undefined
      ) {
        return 'Вы не можете принять участие с этим юр. лицом';
      }
      else {
        return null;
      }
    },

    validateUnit(value: Unit): string | null {
      if (value.id !== this.bid.tender.unit.id) {
        return `Для участия в тендере ед. измерения должна быть "${this.bid.tender.unit.name}"`;
      }
      else {
        return null;
      }
    },
  },
});
</script>